import { Skeleton } from '@mui/material';
import React from 'react';
import { useCatchUp } from '../../api/CatchUpsApi';
import { useDisbursement } from '../../api/DisbursementsApi';
import { usePayment } from '../../api/PaymentsApi';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import {
	BREADCRUMB__INVOICE,
	BREADCRUMB__INVOICES,
} from '../../data/labelsAndMessages';
import { useRouteMatch } from '../../util/useRouteMatch';
import BreadcrumbsContainer, { Breadcrumb } from '../BreadcrumbsContainer';
import {
	CATCHUPS_BREADCRUMB,
	CATCHUPS_BREADCRUMBS__CATCHUP_DISPLAY_ID,
	CATCHUPS_BREADCRUMBS__NEW,
} from '../catchUps/data/constants';
import {
	DISBURSEMENTS_BREADCRUMB,
	DISBURSEMENTS_BREADCRUMB__EDIT,
	DISBURSEMENTS_BREADCRUMB__NEW,
	DISBURSEMENT_BREADCRUMB__DISPLAY_ID,
} from '../disbursements/data/constants';
import {
	PAYMENTS_BREADCRUMBS__EDIT,
	PAYMENTS_BREADCRUMBS__NEW,
	PAYMENTS_BREADCRUMBS__PAYMENTS,
	PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID,
} from '../payments/data/constants';
import { PolicyRoutes, POLICY_ROUTES } from './Sidebar/PolicySidebar';
import { QuoteRoutes, QUOTE_ROUTES } from './Sidebar/QuoteSidebar';

export const POLICY_BREADCRUMBS_HEIGHT = 56;

interface Props {
	policyData: PolicyData;
}

const Breadcrumbs = (props: Props) => {
	const { selectedQuote, policy } = props.policyData;
	const authData = useAuthContext();
	const authToken = authData?.auth?.authorizationToken;
	let policyUrl;
	let policyholderLocator;
	const policyRouteMatch = useRouteMatch(
		Object.keys(POLICY_ROUTES).map(
			(key) => POLICY_ROUTES[key as keyof PolicyRoutes],
		),
	);
	const quoteRouteMatch = useRouteMatch(
		Object.keys(QUOTE_ROUTES).map(
			(key) => QUOTE_ROUTES[key as keyof QuoteRoutes],
		),
	);
	const params = policyRouteMatch?.params || quoteRouteMatch?.params;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const { data: payment, isLoading: isPaymentLoading } = usePayment(
		params?.paymentLocator,
		authToken,
		cashManagementEnabled,
		authData?.hasPermission('payment.fetch'),
	);
	const { data: disbursement, isLoading: isDisbursementLoading } =
		useDisbursement(
			params?.disbursementLocator,
			authToken,
			cashManagementEnabled,
			authData?.hasPermission('disbursement.fetch'),
		);
	const { data: catchUp, isLoading: isCatchUpLoading } = useCatchUp(
		params?.catchUpLocator,
		authToken,
		cashManagementEnabled,
		authData?.hasPermission('catchUpInvoice.fetch'),
	);
	if (selectedQuote && !policy.issuedTimestamp) {
		policyUrl = `/policy/${policy.locator}/quotes/${selectedQuote.locator}`;
		policyholderLocator = selectedQuote.policyholderLocator;
	} else {
		policyUrl = `/policy/${policy.locator}`;
		policyholderLocator = policy.policyholderLocator;
	}

	if (
		(params?.paymentLocator && isPaymentLoading) ||
		(params?.disbursementLocator && isDisbursementLoading) ||
		(params?.catchUpLocator && isCatchUpLoading)
	) {
		return <Skeleton variant='text' width={200} />;
	}

	const breadcrumbs: Breadcrumb[] = [
		{ href: `/policyholder/${policyholderLocator}`, text: 'Policyholder' },
		{ href: policyUrl, text: `Policy ${policy.locator}` },
	];

	// prepare invoices breadcrumbs
	if (
		[POLICY_ROUTES.INVOICE, POLICY_ROUTES.INVOICES].includes(
			policyRouteMatch?.pattern?.path,
		)
	) {
		breadcrumbs.push({
			href: POLICY_ROUTES.INVOICES.replace(':policyId', policy.locator),
			text: BREADCRUMB__INVOICES(),
		});

		if (policyRouteMatch?.pattern?.path === POLICY_ROUTES.INVOICE) {
			breadcrumbs.push({
				href: POLICY_ROUTES.INVOICE.replace(
					':policyId',
					policy.locator,
				).replace(':invoiceId', params.invoiceId),
				text: BREADCRUMB__INVOICE(params.invoiceId),
			});
		}
	}

	if (
		[QUOTE_ROUTES.INVOICE, QUOTE_ROUTES.INVOICES].includes(
			quoteRouteMatch?.pattern?.path,
		)
	) {
		breadcrumbs.push({
			href: QUOTE_ROUTES.INVOICES.replace(':policyId', policy.locator).replace(
				':quoteId',
				selectedQuote.locator,
			),
			text: BREADCRUMB__INVOICES(),
		});

		if (quoteRouteMatch?.pattern?.path === QUOTE_ROUTES.INVOICE) {
			breadcrumbs.push({
				href: QUOTE_ROUTES.INVOICE.replace(':policyId', policy.locator)
					.replace(':quoteId', selectedQuote.locator)
					.replace(':invoiceId', params.invoiceId),
				text: BREADCRUMB__INVOICE(params.invoiceId),
			});
		}
	}

	if (cashManagementEnabled) {
		// prepare payments breadcrumbs
		if (
			[
				POLICY_ROUTES.PAYMENTS,
				POLICY_ROUTES.EDIT_PAYMENT,
				POLICY_ROUTES.NEW_PAYMENT,
				POLICY_ROUTES.PAYMENT,
			].includes(policyRouteMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: POLICY_ROUTES.PAYMENTS.replace(':policyId', policy.locator),
				text: PAYMENTS_BREADCRUMBS__PAYMENTS,
			});
		}

		// prepare disbursements breadcrumbs
		if (
			[
				POLICY_ROUTES.DISBURSEMENTS,
				POLICY_ROUTES.NEW_DISBURSEMENT,
				POLICY_ROUTES.EDIT_DISBURSEMENT,
				POLICY_ROUTES.DISBURSEMENT,
			].includes(policyRouteMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: POLICY_ROUTES.DISBURSEMENTS.replace(':policyId', policy.locator),
				text: DISBURSEMENTS_BREADCRUMB,
			});
		}

		// prepare catch ups breadcrumbs
		if (
			[
				POLICY_ROUTES.CATCHUPS,
				POLICY_ROUTES.NEW_CATCHUP,
				POLICY_ROUTES.CATCHUP,
			].includes(policyRouteMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: POLICY_ROUTES.CATCHUPS.replace(':policyId', policy.locator),
				text: CATCHUPS_BREADCRUMB,
			});
		}

		if (
			[
				QUOTE_ROUTES.PAYMENTS,
				QUOTE_ROUTES.EDIT_PAYMENT,
				QUOTE_ROUTES.NEW_PAYMENT,
				QUOTE_ROUTES.PAYMENT,
			].includes(quoteRouteMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: QUOTE_ROUTES.PAYMENTS.replace(
					':policyId',
					policy.locator,
				).replace(':quoteId', params.quoteId),
				text: PAYMENTS_BREADCRUMBS__PAYMENTS,
			});
		}

		switch (policyRouteMatch?.pattern?.path) {
			case POLICY_ROUTES.EDIT_PAYMENT: {
				breadcrumbs.push({
					href: POLICY_ROUTES.PAYMENT.replace(
						':policyId',
						policy.locator,
					).replace(':paymentLocator', params?.paymentLocator),
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__EDIT,
				});
				break;
			}
			case POLICY_ROUTES.NEW_PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__NEW,
				});
				break;
			}
			case POLICY_ROUTES.PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				break;
			}
			case POLICY_ROUTES.NEW_DISBURSEMENT: {
				breadcrumbs.push({
					text: DISBURSEMENTS_BREADCRUMB__NEW,
				});
				break;
			}
			case POLICY_ROUTES.DISBURSEMENT: {
				breadcrumbs.push({
					text: DISBURSEMENT_BREADCRUMB__DISPLAY_ID(disbursement?.displayId),
				});
				break;
			}
			case POLICY_ROUTES.EDIT_DISBURSEMENT: {
				breadcrumbs.push({
					href: POLICY_ROUTES.DISBURSEMENT.replace(
						':policyId',
						policy.locator,
					).replace(':disbursementLocator', params?.disbursementLocator),
					text: DISBURSEMENT_BREADCRUMB__DISPLAY_ID(disbursement?.displayId),
				});
				breadcrumbs.push({
					text: DISBURSEMENTS_BREADCRUMB__EDIT,
				});
				break;
			}
			case POLICY_ROUTES.NEW_CATCHUP: {
				breadcrumbs.push({
					text: CATCHUPS_BREADCRUMBS__NEW,
				});
				break;
			}
			case POLICY_ROUTES.CATCHUP: {
				breadcrumbs.push({
					text: CATCHUPS_BREADCRUMBS__CATCHUP_DISPLAY_ID(catchUp?.displayId),
				});
				break;
			}
		}

		switch (quoteRouteMatch?.pattern?.path) {
			case QUOTE_ROUTES.EDIT_PAYMENT: {
				breadcrumbs.push({
					href: QUOTE_ROUTES.PAYMENT.replace(':policyId', policy.locator)
						.replace(':quoteId', params?.quoteId)
						.replace(':paymentLocator', params?.paymentLocator),
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__EDIT,
				});
				break;
			}
			case QUOTE_ROUTES.NEW_PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__NEW,
				});
				break;
			}
			case QUOTE_ROUTES.PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				break;
			}
		}
	}

	return <BreadcrumbsContainer breadcrumbs={breadcrumbs} />;
};

export default withPolicyContext<Props>(Breadcrumbs);
